import { PRIORITY_CONFIG, STATUS_CONFIG } from "./statusConfig";
import { useCallback, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

// The portal's position in the URL. It is mounted on a splat route, so the
// tab and the open wizard live in the path — the first splat segment is the
// tab, the second is the wizard — and only the filters live in the query
// string. Every mapping is a pure function so it is testable without a
// router; the hook at the bottom is the only part that needs one.

const ALL_TAB_SEGMENT = "all";
const NEW_TAB_SEGMENT = "new";

const WIZARD_BY_SEGMENT = {
  add: "NEW_TEMPLATE",
  edit: "EDIT_TEMPLATE",
  bulk: "BULK_REVIEW",
};

const SEGMENT_BY_WIZARD = {
  NEW_TEMPLATE: "add",
  EDIT_TEMPLATE: "edit",
  BULK_REVIEW: "bulk",
};

const STATUS_PARAM = "status";
const PRIORITY_PARAM = "priority";

// The first splat segment. Anything unrecognised, including an absent
// segment, lands on the first tab, which is today's landing.
export const tabFromSegment = (segment) =>
  segment === ALL_TAB_SEGMENT ? 1 : 0;

export const segmentFromTab = (tab) =>
  Number(tab) === 1 ? ALL_TAB_SEGMENT : NEW_TAB_SEGMENT;

// The second splat segment, naming the open wizard by its request type.
export const wizardFromSegment = (segment) =>
  WIZARD_BY_SEGMENT[segment] || null;

export const segmentFromWizard = (requestType) =>
  SEGMENT_BY_WIZARD[requestType] || null;

// The pathname the portal is mounted at, derived rather than hard-coded
// because the component is reachable from a second splat mount point.
export const basePath = (pathname, splat) => {
  const path = pathname || "";
  const tail = splat || "";
  const base =
    tail && path.endsWith(tail)
      ? path.slice(0, path.length - tail.length)
      : path;
  return base.replace(/\/+$/, "");
};

export const portalPath = (base, tab, wizard) => {
  const wizardSegment = segmentFromWizard(wizard);
  return `${base}/${segmentFromTab(tab)}${
    wizardSegment ? `/${wizardSegment}` : ""
  }`;
};

// A comma-separated list of keys, validated against a config object keyed by
// the same keys. Unknown keys are discarded on read so an inbound link cannot
// filter on a status that no longer exists.
export const parseKeyList = (value, allowed) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((key) => key.trim())
    .filter((key) => Object.prototype.hasOwnProperty.call(allowed, key));
};

export const formatKeyList = (keys) => (keys || []).join(",");

export const usePortalUrlState = () => {
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Rendered outside the splat route — as the history component's own tests
  // do — there is no splat at all, which reads as an empty one.
  const splat = params["*"] || "";

  const base = useMemo(() => basePath(pathname, splat), [pathname, splat]);
  const segments = useMemo(() => splat.split("/").filter(Boolean), [splat]);

  const tab = tabFromSegment(segments[0]);
  const wizard = wizardFromSegment(segments[1]);

  const statuses = useMemo(
    () => parseKeyList(searchParams.get(STATUS_PARAM), STATUS_CONFIG),
    [searchParams]
  );
  const priorities = useMemo(
    () => parseKeyList(searchParams.get(PRIORITY_PARAM), PRIORITY_CONFIG),
    [searchParams]
  );

  const search = useMemo(() => {
    const query = searchParams.toString();
    return query ? `?${query}` : "";
  }, [searchParams]);

  // A tab and a wizard are places, so a move pushes and carries the filters
  // across unchanged. Both halves of the position are written by one call, so
  // a caller that changes both — a submission closes its wizard and lands on
  // the history tab — makes one history entry rather than two, and Back from
  // it does not step through a half-applied position.
  //
  // `replace` is for a move away from a place the user cannot return to: a
  // submitted wizard is spent, so it is overwritten rather than left on the
  // stack for Back to reopen empty.
  const setPosition = useCallback(
    (nextTab, nextWizard, { replace = false } = {}) =>
      navigate(
        { pathname: portalPath(base, nextTab, nextWizard), search },
        { replace }
      ),
    [base, navigate, search]
  );

  const setTab = useCallback((nextTab) => setPosition(nextTab, wizard), [
    setPosition,
    wizard,
  ]);

  const setWizard = useCallback((nextWizard) => setPosition(tab, nextWizard), [
    setPosition,
    tab,
  ]);

  // A filter is not a place: each write replaces, so Back leaves the portal
  // instead of stepping through every filter tried. Each write preserves the
  // parameter it does not own.
  //
  // The updater form reads the URL as it stands when the write is issued, not
  // a queued previous value as `setState` would, so two writes in one tick
  // would lose the first. Every caller is a click, which is its own tick.
  const setKeyParam = useCallback(
    (name, allowed, next) =>
      setSearchParams(
        (current) => {
          const updated = new URLSearchParams(current);
          const keys =
            typeof next === "function"
              ? next(parseKeyList(updated.get(name), allowed))
              : next;
          const value = formatKeyList(keys);
          if (value) updated.set(name, value);
          else updated.delete(name);
          return updated;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const setStatuses = useCallback(
    (next) => setKeyParam(STATUS_PARAM, STATUS_CONFIG, next),
    [setKeyParam]
  );

  const setPriorities = useCallback(
    (next) => setKeyParam(PRIORITY_PARAM, PRIORITY_CONFIG, next),
    [setKeyParam]
  );

  return {
    tab,
    setTab,
    wizard,
    setWizard,
    setPosition,
    statuses,
    setStatuses,
    priorities,
    setPriorities,
  };
};
